import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useT } from "../../contexts/LanguageContext";
import { ChatCircleDots, X } from "@phosphor-icons/react";

/**
 * FeedbackWidget — floating button (bottom-left) that lets logged-in users
 * submit beta feedback. Sends to /api/feedback and admins see it in Admin panel.
 */
export default function FeedbackWidget() {
  const { user } = useAuth();
  const { lang } = useT();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(4);
  const [sending, setSending] = useState(false);

  // Only for logged-in users
  if (!user) return null;

  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error(lang === "tr" ? "Mesaj çok kısa" : "Message too short");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        category, message: message.trim(), rating,
        page: window.location.pathname,
      });
      toast.success(lang === "tr" ? "Geri bildirimin alındı, teşekkürler! 🙏" : "Feedback received, thanks! 🙏");
      setMessage(""); setRating(4); setOpen(false);
    } catch {
      toast.error(lang === "tr" ? "Bir hata oluştu" : "Something went wrong");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 h-12 px-4 rounded-full bg-[#002FA7] text-white shadow-lg hover:bg-[#003DD6] flex items-center gap-2 text-sm font-semibold transition-transform hover:scale-105"
        data-testid="feedback-widget-button"
        aria-label={lang === "tr" ? "Geri bildirim gönder" : "Send feedback"}
      >
        <ChatCircleDots size={18} weight="fill" />
        {lang === "tr" ? "Geri bildirim" : "Feedback"}
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
          data-testid="feedback-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-bold">
                  {lang === "tr" ? "Bize geri bildirim ver" : "Send us feedback"}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {lang === "tr"
                    ? "PMCoach AI hâlâ betada. Her yorumun geliştirmemize direkt katkı sağlıyor."
                    : "PMCoach AI is still in beta. Every note helps us improve."}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="feedback-close">
                <X size={20} />
              </button>
            </div>

            {/* Category */}
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                {lang === "tr" ? "Kategori" : "Category"}
              </label>
              <div className="mt-2 flex gap-2 flex-wrap">
                {[
                  { key: "bug", tr: "🐞 Hata", en: "🐞 Bug" },
                  { key: "idea", tr: "💡 Fikir", en: "💡 Idea" },
                  { key: "praise", tr: "❤️ Beğeni", en: "❤️ Praise" },
                  { key: "other", tr: "💬 Diğer", en: "💬 Other" },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      category === c.key
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`feedback-category-${c.key}`}
                  >
                    {lang === "tr" ? c.tr : c.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Rating (only for praise/idea) */}
            {(category === "praise" || category === "idea") && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  {lang === "tr" ? "Genel puan" : "Overall rating"}
                </label>
                <div className="mt-2 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      className={`h-9 w-9 rounded-md text-lg ${
                        n <= rating ? "bg-yellow-400 text-white" : "bg-slate-100 text-slate-400"
                      }`}
                      data-testid={`feedback-rating-${n}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                {lang === "tr" ? "Ne düşünüyorsun?" : "What's on your mind?"}
              </label>
              <textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  lang === "tr"
                    ? "Detaylı yazabilirsin. Sayfa bilgisi otomatik gönderilecek."
                    : "Feel free to be detailed. Page info is included automatically."
                }
                className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="feedback-message"
              />
            </div>

            <button
              onClick={submit}
              disabled={sending}
              className="mt-4 w-full btn-primary disabled:opacity-50"
              data-testid="feedback-submit"
            >
              {sending ? "..." : lang === "tr" ? "Gönder" : "Send"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
